import { useState, useEffect, useCallback, memo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, X, Trash2, Tag, Mail, MessageSquare, Send, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useCommsGate } from '@/components/CommsUpgradeDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Standard service types for consistency (shown on customer invoice)
const SERVICE_TYPES = [
  { value: 'minor_service', label: 'Minor Service' },
  { value: 'intermediate_service', label: 'Intermediate Service' },
  { value: 'major_service', label: 'Major Service' },
  { value: 'logbook_service', label: 'Logbook Service' },
  { value: 'general_service', label: 'General Service' },
  { value: 'oil_change', label: 'Oil Change' },
  { value: 'brake_service', label: 'Brake Service' },
  { value: 'tyre_service', label: 'Tyre Service' },
  { value: 'wheel_alignment', label: 'Wheel Alignment' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'timing_belt', label: 'Timing Belt Service' },
  { value: 'air_conditioning', label: 'Air Conditioning Service' },
  { value: 'battery_replacement', label: 'Battery Replacement' },
  { value: 'clutch_service', label: 'Clutch Service' },
  { value: 'transmission_service', label: 'Transmission Service' },
  { value: 'cooling_system', label: 'Cooling System Service' },
  { value: 'exhaust', label: 'Exhaust Repair' },
  { value: 'electrical', label: 'Electrical Repair' },
  { value: 'diagnostic', label: 'Diagnostic Check' },
  { value: 'pre_purchase', label: 'Pre-Purchase Inspection' },
  { value: 'rwc', label: 'Roadworthy Certificate (RWC)' },
  { value: 'other', label: 'Other' },
];

// Service categories for INTERNAL tracking (NOT shown on customer invoice)
// Used by Super Admin for reconciliation and reporting
const SERVICE_CATEGORIES = [
  { value: 'general_service', label: 'General Service' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'aircon', label: 'Air Conditioning' },
  { value: 'rwc', label: 'RWC (Roadworthy)' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'transmission', label: 'Transmission' },
  { value: 'clutch', label: 'Clutch' },
  { value: 'timing_belt', label: 'Timing Belt' },
  { value: 'cooling_system', label: 'Cooling System' },
  { value: 'exhaust', label: 'Exhaust' },
  { value: 'battery', label: 'Battery' },
  { value: 'diagnostic', label: 'Diagnostic' },
  { value: 'other', label: 'Other' },
];

// Memoized Line Item Row component
const LineItemRow = memo(function LineItemRow({ item, index, onUpdate, onRemove, showRemove, isFirst }) {
  const [localDescription, setLocalDescription] = useState(item.description);
  const [localQuantity, setLocalQuantity] = useState(item.quantity);
  const [localUnitPrice, setLocalUnitPrice] = useState(item.unit_price);
  const [localDiscount, setLocalDiscount] = useState(item.discount_percent);

  useEffect(() => {
    setLocalDescription(item.description);
    setLocalQuantity(item.quantity);
    setLocalUnitPrice(item.unit_price);
    setLocalDiscount(item.discount_percent);
  }, [item.description, item.quantity, item.unit_price, item.discount_percent]);

  const handleBlur = (field, value) => {
    onUpdate(index, field, value);
  };

  const itemTotal = (parseFloat(localQuantity) || 0) * (parseFloat(localUnitPrice) || 0) * (1 - (parseFloat(localDiscount) || 0) / 100);

  return (
    <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-blue-50/50 transition-colors`}>
      <td className="px-4 py-3 border-b border-slate-200">
        <Input
          value={localDescription}
          onChange={(e) => setLocalDescription(e.target.value)}
          onBlur={() => handleBlur('description', localDescription)}
          placeholder="Enter item description..."
          className="border-0 bg-transparent focus:ring-2 focus:ring-blue-500 h-10 text-base w-full"
        />
      </td>
      <td className="px-3 py-3 border-b border-slate-200 w-20 text-center">
        <Input
          type="number"
          value={localQuantity}
          onChange={(e) => setLocalQuantity(e.target.value)}
          onBlur={() => handleBlur('quantity', parseInt(localQuantity) || 0)}
          min="1"
          className="border-0 bg-transparent focus:ring-2 focus:ring-blue-500 h-10 text-center text-base"
        />
      </td>
      <td className="px-3 py-3 border-b border-slate-200 w-32">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
          <Input
            type="number"
            value={localUnitPrice}
            onChange={(e) => setLocalUnitPrice(e.target.value)}
            onBlur={() => handleBlur('unit_price', parseFloat(localUnitPrice) || 0)}
            min="0"
            step="0.01"
            className="border-0 bg-transparent focus:ring-2 focus:ring-blue-500 h-10 pl-7 text-base"
          />
        </div>
      </td>
      <td className="px-3 py-3 border-b border-slate-200 w-24">
        <div className="relative">
          <Input
            type="number"
            value={localDiscount}
            onChange={(e) => setLocalDiscount(e.target.value)}
            onBlur={() => handleBlur('discount_percent', parseFloat(localDiscount) || 0)}
            min="0"
            max="100"
            className="border-0 bg-transparent focus:ring-2 focus:ring-blue-500 h-10 pr-7 text-center text-base"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">%</span>
        </div>
      </td>
      <td className="px-4 py-3 border-b border-slate-200 w-32 text-right font-bold text-slate-800 text-base">
        ${itemTotal.toFixed(2)}
      </td>
      <td className="px-2 py-3 border-b border-slate-200 w-14 text-center">
        {showRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemove(index)}
            className="h-9 w-9 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-5 w-5" />
          </Button>
        )}
      </td>
    </tr>
  );
});

export default function InvoiceForm({ 
  vehicle, 
  customer, 
  initialData,
  serviceSchedules = [],
  onSubmit, 
  onCancel,
  submitText = "Create Invoice",
  onScheduleSelect,
  invoiceId = null,  // For sending invoice via email/SMS
  showSendButtons = false  // Show Email/SMS buttons after save
}) {
  // Local form state to prevent focus issues
  const [serviceType, setServiceType] = useState(initialData?.service_type || '');
  const [odometer, setOdometer] = useState(initialData?.odometer_reading || '');
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [status, setStatus] = useState(initialData?.status || 'draft');
  const [lineItems, setLineItems] = useState(
    initialData?.line_items?.length > 0 
      ? initialData.line_items.map(item => ({ ...item, id: item.id || crypto.randomUUID() }))
      : [{ id: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0, discount_percent: 0, gst_included: true }]
  );
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  
  // Service categories for internal tracking (multi-select)
  const [serviceCategories, setServiceCategories] = useState(initialData?.service_categories || []);
  
  // SMS Template Modal State
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const { guardComms, commsDialog } = useCommsGate();
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Sync when initialData changes (e.g., editing mode)
  useEffect(() => {
    if (initialData) {
      setServiceType(initialData.service_type || '');
      setOdometer(initialData.odometer_reading || '');
      setNotes(initialData.notes || '');
      setStatus(initialData.status || 'draft');
      setServiceCategories(initialData.service_categories || []);
      if (initialData.line_items?.length > 0) {
        setLineItems(initialData.line_items.map(item => ({ ...item, id: item.id || crypto.randomUUID() })));
      }
    }
  }, [initialData]);

  // Fetch SMS templates when modal opens
  useEffect(() => {
    if (smsModalOpen) {
      fetchSmsTemplates();
    }
  }, [smsModalOpen]);

  const fetchSmsTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/marketing/templates?channel=sms`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSmsTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch SMS templates:', error);
    }
  };

  const handleSendEmail = async () => {
    if (!invoiceId || !customer?.email) {
      toast.error(customer?.email ? 'Invoice must be saved first' : 'Customer has no email address');
      return;
    }
    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/send-email`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Invoice emailed successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleSendSms = async () => {
    if (!invoiceId || !customer?.phone) {
      toast.error(customer?.phone ? 'Invoice must be saved first' : 'Customer has no phone number');
      return;
    }
    setSmsModalOpen(true);
  };

  const sendSmsWithTemplate = async () => {
    const template = smsTemplates.find(t => t.template_id === selectedTemplate);
    const message = template ? 
      template.content
        .replace(/{customer_name}/g, customer?.display_name || customer?.first_name || 'Customer')
        .replace(/{vehicle_rego}/g, vehicle?.rego || '')
        .replace(/{invoice_total}/g, `$${calculateTotal().toFixed(2)}`)
        .replace(/{invoice_id}/g, invoiceId || '')
      : customMessage;

    if (!message.trim()) {
      toast.error('Please select a template or write a custom message');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/send-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message })
      });
      if (response.ok) {
        toast.success('SMS sent successfully');
        setSmsModalOpen(false);
        setSelectedTemplate('');
        setCustomMessage('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Failed to send SMS:', error);
      toast.error('Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  // Toggle service category selection
  const toggleServiceCategory = useCallback((categoryValue) => {
    setServiceCategories(prev => {
      if (prev.includes(categoryValue)) {
        return prev.filter(c => c !== categoryValue);
      } else {
        return [...prev, categoryValue];
      }
    });
  }, []);

  const addLineItem = useCallback(() => {
    setLineItems(prev => [...prev, { 
      id: crypto.randomUUID(), 
      description: '', 
      quantity: 1, 
      unit_price: 0, 
      discount_percent: 0, 
      gst_included: true 
    }]);
  }, []);

  const removeLineItem = useCallback((index) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateLineItem = useCallback((index, field, value) => {
    setLineItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  }, []);

  const handleScheduleSelect = (scheduleId) => {
    const schedule = serviceSchedules.find(s => s.schedule_id === scheduleId);
    if (!schedule) return;
    
    setSelectedScheduleId(scheduleId);
    
    // Map schedule service level to our dropdown values
    const serviceLevel = schedule.service_level || 'general_service';
    const serviceTypeMap = {
      'minor': 'minor_service',
      'intermediate': 'intermediate_service',
      'major': 'major_service',
      'logbook': 'logbook_service'
    };
    setServiceType(serviceTypeMap[serviceLevel] || 'general_service');
    
    setLineItems(schedule.line_items.map(item => ({
      id: crypto.randomUUID(),
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_percent: item.discount_percent || 0,
      gst_included: item.gst_included !== false
    })));
    
    if (onScheduleSelect) {
      onScheduleSelect(schedule);
    }
  };

  // GST Inclusive calculations
  // All prices include GST, so:
  // Subtotal = sum of line items (includes GST)
  // GST = Subtotal / 11 (for 10% GST - just shows the GST component)
  // Total = Subtotal (same value, GST is already included)
  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => {
      return sum + (item.quantity || 0) * (item.unit_price || 0) * (1 - (item.discount_percent || 0) / 100);
    }, 0);
  };

  const calculateGST = () => {
    return calculateSubtotal() / 11; // GST component included in subtotal
  };

  const calculateTotal = () => {
    return calculateSubtotal(); // Total equals subtotal (GST already included)
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert service type value to label for storage
    const serviceTypeLabel = SERVICE_TYPES.find(t => t.value === serviceType)?.label || serviceType;
    onSubmit({
      service_type: serviceTypeLabel,
      odometer_reading: odometer,
      notes: notes,
      status: status,
      service_categories: serviceCategories, // Internal tracking categories (not shown on customer invoice)
      line_items: lineItems.map(({ id, ...rest }) => rest) // Remove id before submitting
    });
  };

  const selectedSchedule = serviceSchedules.find(s => s.schedule_id === selectedScheduleId);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Vehicle & Customer Summary */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Vehicle</p>
            <p className="font-bold text-lg text-slate-900">{vehicle?.rego}</p>
            <p className="text-sm text-slate-600">{vehicle?.make} {vehicle?.model}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Customer</p>
            <p className="font-bold text-lg text-slate-900">
              {customer?.display_name || `${customer?.first_name} ${customer?.last_name}`}
            </p>
            <p className="text-sm text-slate-600">{customer?.phone}</p>
          </div>
        </div>
      </div>

      {/* Service Schedule Selection */}
      {serviceSchedules.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <Label className="text-blue-800 font-semibold mb-3 block text-sm">
            Quick Service Selection (Based on {vehicle?.make} {vehicle?.model})
          </Label>
          <Select value={selectedScheduleId} onValueChange={handleScheduleSelect}>
            <SelectTrigger className="h-11 bg-white border-blue-200" data-testid="service-schedule-select">
              <SelectValue placeholder="Select a service interval to auto-populate items..." />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {serviceSchedules.map(schedule => (
                <SelectItem key={schedule.schedule_id} value={schedule.schedule_id}>
                  <div className="flex items-center justify-between w-full gap-4">
                    <span className="font-medium">{schedule.service_name}</span>
                    <span className="text-slate-500 text-sm">~${schedule.estimated_total?.toFixed(2)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedSchedule && (
            <p className="text-xs text-blue-600 mt-2">
              {selectedSchedule.description} • {selectedSchedule.line_items?.length} items loaded
            </p>
          )}
        </div>
      )}

      {/* Service Details */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-1">
          <Label htmlFor="service_type" className="text-sm font-medium">Service Type</Label>
          <Select value={serviceType} onValueChange={setServiceType}>
            <SelectTrigger className="h-11 mt-1" id="service_type">
              <SelectValue placeholder="Select service type..." />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {SERVICE_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-1">
          <Label htmlFor="odometer" className="text-sm font-medium">Odometer (km)</Label>
          <Input
            id="odometer"
            type="number"
            value={odometer}
            onChange={(e) => setOdometer(e.target.value)}
            placeholder="e.g., 85000"
            className="h-11 mt-1"
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="status" className="text-sm font-medium">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Service Categories - INTERNAL USE ONLY (NOT shown on customer invoice) */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="h-4 w-4 text-amber-600" />
          <Label className="text-amber-800 font-semibold text-sm">
            Service Categories (Internal Tracking)
          </Label>
        </div>
        <p className="text-xs text-amber-600 mb-3">
          Select all applicable service categories. Used for Super Admin reporting &amp; reconciliation. 
          <span className="font-medium"> NOT shown on customer invoice.</span>
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {SERVICE_CATEGORIES.map(category => (
            <div key={category.value} className="flex items-center space-x-2">
              <Checkbox
                id={`cat-${category.value}`}
                checked={serviceCategories.includes(category.value)}
                onCheckedChange={() => toggleServiceCategory(category.value)}
                data-testid={`service-category-${category.value}`}
              />
              <Label 
                htmlFor={`cat-${category.value}`} 
                className="text-sm cursor-pointer text-slate-700 leading-tight"
              >
                {category.label}
              </Label>
            </div>
          ))}
        </div>
        {serviceCategories.length > 0 && (
          <div className="mt-3 pt-3 border-t border-amber-200">
            <p className="text-xs text-amber-700">
              <span className="font-medium">Selected:</span> {serviceCategories.map(c => SERVICE_CATEGORIES.find(cat => cat.value === c)?.label).join(', ')}
            </p>
          </div>
        )}
      </div>

      {/* Line Items Table */}
      <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="bg-slate-800 px-5 py-4 flex items-center justify-between">
          <h3 className="text-white font-bold text-lg">Line Items</h3>
          <Button 
            type="button" 
            variant="outline" 
            size="sm" 
            onClick={addLineItem}
            className="bg-white hover:bg-slate-100 text-slate-800 border-0 font-semibold"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Item
          </Button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-4 text-left text-sm font-bold text-slate-700 uppercase tracking-wider">Description</th>
                <th className="px-3 py-4 text-center text-sm font-bold text-slate-700 uppercase tracking-wider w-20">Qty</th>
                <th className="px-3 py-4 text-left text-sm font-bold text-slate-700 uppercase tracking-wider w-32">Price (inc GST)</th>
                <th className="px-3 py-4 text-center text-sm font-bold text-slate-700 uppercase tracking-wider w-24">Discount</th>
                <th className="px-4 py-4 text-right text-sm font-bold text-slate-700 uppercase tracking-wider w-32">Line Total</th>
                <th className="px-2 py-4 w-14"></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, index) => (
                <LineItemRow
                  key={item.id}
                  item={item}
                  index={index}
                  onUpdate={updateLineItem}
                  onRemove={removeLineItem}
                  showRemove={lineItems.length > 1}
                  isFirst={index === 0}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="bg-slate-50 border-t border-slate-200 p-5">
          <div className="flex justify-end">
            <div className="w-80 space-y-3">
              <div className="flex justify-between text-base">
                <span className="text-slate-600">Subtotal (inc GST):</span>
                <span className="font-semibold">${calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-base text-slate-500">
                <span>GST (included):</span>
                <span className="font-medium">${calculateGST().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold border-t-2 border-slate-300 pt-3 mt-3">
                <span>Total:</span>
                <span className="text-blue-600">${calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <Label htmlFor="notes" className="text-sm font-medium">Notes / Comments</Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any additional notes or special instructions..."
          rows={3}
          className="mt-1 resize-none"
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200">
        <Button 
          type="submit" 
          className="flex-1 min-w-[150px] h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          data-testid="invoice-save-btn"
        >
          {submitText}
        </Button>
        {showSendButtons && (
          <>
            <Button 
              type="button" 
              variant="outline" 
              onClick={guardComms(handleSendEmail)}
              disabled={sending || !customer?.email}
              className="h-12 px-4"
              data-testid="invoice-email-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={guardComms(handleSendSms)}
              disabled={sending || !customer?.phone}
              className="h-12 px-4"
              data-testid="invoice-sms-btn"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              SMS
            </Button>
          </>
        )}
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          className="h-12 px-6"
        >
          Cancel
        </Button>
      </div>

      {/* SMS Template Selection Modal */}
      <Dialog open={smsModalOpen} onOpenChange={setSmsModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Send Invoice via SMS
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-slate-700">Sending to:</p>
              <p className="text-slate-600">{customer?.display_name || customer?.first_name} - {customer?.phone}</p>
            </div>

            {smsTemplates.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Select Template</Label>
                <Select value={selectedTemplate} onValueChange={(val) => {
                  setSelectedTemplate(val);
                  setCustomMessage('');
                }}>
                  <SelectTrigger data-testid="sms-template-select">
                    <SelectValue placeholder="Choose a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {smsTemplates.map(template => (
                      <SelectItem key={template.template_id} value={template.template_id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTemplate && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <p className="text-blue-700">
                      {smsTemplates.find(t => t.template_id === selectedTemplate)?.content
                        .replace(/{customer_name}/g, customer?.display_name || customer?.first_name || 'Customer')
                        .replace(/{vehicle_rego}/g, vehicle?.rego || '')
                        .replace(/{invoice_total}/g, `$${calculateTotal().toFixed(2)}`)
                        .replace(/{invoice_id}/g, invoiceId || '')}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {smsTemplates.length > 0 ? 'Or write a custom message' : 'Message'}
              </Label>
              <Textarea
                value={customMessage}
                onChange={(e) => {
                  setCustomMessage(e.target.value);
                  setSelectedTemplate('');
                }}
                placeholder="Type your message here..."
                rows={4}
                className="resize-none"
                data-testid="sms-custom-message"
              />
              <p className="text-xs text-slate-500">
                Available placeholders: {'{customer_name}'}, {'{vehicle_rego}'}, {'{invoice_total}'}, {'{invoice_id}'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendSmsWithTemplate} 
              disabled={sending || (!selectedTemplate && !customMessage.trim())}
              data-testid="send-sms-confirm-btn"
            >
              {sending ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {commsDialog}
    </form>
  );
}
